import type { User } from "@productlane/db"
import type { ConnectorAttribution } from "@productlane/lib/src/ai"
import type {
	ChatMessage,
	Comment,
	Company,
	CompanyMeta,
	Contact,
	CustomerNeed,
	Email,
	EmailContent,
	Feedback,
	Issue,
	LinearUser,
	LiveChatMessage,
	LiveChatSession,
	Project,
	SlackMessage,
	SlackUser,
	Tag,
	ThreadActivityRow,
	ThreadMessage,
	Video,
	Workspace,
} from "@productlane/zero"
import { isImportedSlackChannel } from "@productlane/lib/src/imported-thread"
import { owesReplyAgainstDeadline } from "@productlane/lib/src/sla-window"
import { parseThreadActivity } from "@productlane/lib/src/thread-activity-schemas"

import type { ActivityEvent } from "./activity-aggregation"
import {
	aggregateActivityEvents,
	collapseAgentActivityBursts,
	collapseRoutineRuns,
} from "./activity-aggregation"
import { normalizeEmail } from "./contact"
import { EmailContactSchema } from "./email"
import { generateNameFromEmail } from "./name-from-email"

export type LiveChatSessionWithMessages = LiveChatSession & {
	messages: readonly LiveChatMessage[]
}

export type ProjectWithCustomerNeeds = Project & {
	customerNeeds: readonly CustomerNeed[]
}

export type IssueWithCustomerNeeds = Issue & {
	customerNeeds: readonly CustomerNeed[]
}

export type AugmentedFeedback = Feedback & {
	tags: readonly Tag[]
	video?: Video
	projects: readonly ProjectWithCustomerNeeds[]
	issues: readonly IssueWithCustomerNeeds[]
	contact?: Contact
	company?: Company & { companyMeta?: CompanyMeta }
	comments: readonly Comment[] | undefined
	emails: readonly Email[] | undefined
	threadMessages: readonly ThreadMessage[] | undefined
	slackMessages: readonly SlackMessage[] | undefined
	chatMessages: readonly ChatMessage[] | undefined
	liveChatSession?: LiveChatSessionWithMessages
	threadActivities: readonly ThreadActivityRow[] | undefined
}

export type TimelineEvent =
	| {
			type: "createdAt"
			thread: Feedback & {
				creatorName?: string
				creatorImage?: string | null
			}
	  }
	| {
			type: "email"
			email: Email & {
				// The body, loaded only by the thread detail queries.
				content?: EmailContent | null
				fromContactName: string
				fromContactImage?: string | null
				fromContactId?: string | null
				isLast: boolean
			}
			connectorSources?: readonly ConnectorAttribution[]
	  }
	| {
			type: "comment"
			comment: Comment & {
				userName?: string | null
				userImage?: string | null
			}
			replies: (Comment & {
				userName?: string | null
				userImage?: string | null
			})[]
	  }
	| {
			type: "live-chat-message"
			liveChatMessage: LiveChatMessage
			contactIsUnverified: boolean
	  }
	| {
			type: "live-chat-closed"
			liveChat: LiveChatSessionWithMessages
	  }
	| {
			type: "threadMessage"
			threadMessage: ThreadMessage
	  }
	| {
			type: "slackMessage"
			slackMessage: SlackMessage & {
				fromContactName: string
				fromContactImage?: string | null
				fromContactId?: string | null
				fromContactEmail?: string | null
			}
			isAgentReply: boolean
			isFromAgent: boolean
	  }
	| {
			type: "chatMessage"
			chatMessage: ChatMessage & {
				fromContactName: string
				fromContactImage?: string | null
			}
			isAgentReply: boolean
			isFromAgent: boolean
	  }
	| {
			type: "activity"
			threadActivity: ThreadActivityRow & {
				actorName?: string | null
				actorImage?: string | null
				newAssigneeName?: string | null
			}
	  }
	| {
			type: "agentActivityBurst"
			burst: {
				id: string
				createdAt: number
				/** Oldest member, so a partly synced burst stays below the gapless cut. */
				firstCreatedAt: number
				events: ActivityEvent[]
			}
	  }
	| {
			type: "routineRun"
			run: {
				id: string
				createdAt: number
				runActivity: ActivityEvent
				/** The tag/importance/assignee rows this run produced. */
				events: ActivityEvent[]
			}
	  }

export function timelineEventTimestamp(event: TimelineEvent): number {
	switch (event.type) {
		case "createdAt":
			return event.thread.createdAt
		case "email":
			return event.email.createdAt
		case "comment":
			return event.comment.createdAt
		case "threadMessage":
			return event.threadMessage.createdAt
		case "slackMessage":
			return event.slackMessage.createdAt
		case "chatMessage":
			return event.chatMessage.createdAt
		case "live-chat-message":
			return event.liveChatMessage.createdAt
		case "live-chat-closed":
			return event.liveChat.finishedAt || Date.now()
		case "activity":
			return event.threadActivity.createdAt
		case "agentActivityBurst":
			return event.burst.createdAt
		case "routineRun":
			return event.run.createdAt
	}
}

// The Slack envelope stays out of the Zero schema, so clients fall back to the
// temp id our own writes carry before Slack acknowledges them.
interface SlackMessageWithRaw {
	raw?: unknown
}

/** True when we posted the row into Slack ourselves, so its Slack identity is our app rather than the author. */
export function isProductlaneMirroredSlackMessage(
	slackMessage: Pick<SlackMessage, "slackId"> & SlackMessageWithRaw,
): boolean {
	if (slackMessage.slackId.startsWith("temp_")) return true
	const raw = slackMessage.raw as
		| { event?: { metadata?: { event_type?: string } } }
		| null
		| undefined
	return raw?.event?.metadata?.event_type === "productlane_message"
}

// Zero leaves `raw` off the synced row, so the dashboard knows an import by its synthetic channel.
function isImportedSlackMessage(
	slackMessage: SlackMessageWithRaw & { channelId?: string | null },
): boolean {
	return (
		isImportedSlackChannel(slackMessage.channelId) ||
		(slackMessage.raw as { imported?: unknown } | null)?.imported === true
	)
}

export function resolveSlackMessageSender({
	slackMessage,
	usersById,
	slackUsersBySlackId,
	threadContact,
}: {
	slackMessage: Pick<
		SlackMessage,
		"type" | "userId" | "slackUserId" | "slackId"
	> &
		Partial<Pick<SlackMessage, "channelId">> &
		SlackMessageWithRaw
	usersById: Map<string, Pick<User, "id" | "name" | "imageUrl">>
	slackUsersBySlackId: Map<string, SlackUser>
	threadContact: Contact | undefined
}): {
	fromContactName?: string | null
	fromContactImage?: string | null
	fromContactId?: string | null
	fromContactEmail?: string | null
} {
	const slackUser = slackMessage.slackUserId
		? slackUsersBySlackId.get(slackMessage.slackUserId)
		: undefined

	if (slackMessage.type === "OUTBOUND") {
		const user = slackMessage.userId
			? usersById.get(slackMessage.userId)
			: undefined
		if (user) {
			return {
				fromContactName: user.name,
				fromContactImage: user.imageUrl,
				fromContactId: user.id,
			}
		}
		// A teammate replying in Slack itself carries only their Slack author id.
		if (slackUser) {
			return {
				fromContactName: slackUser.name ?? slackUser.username,
				fromContactImage: slackUser.imageUrl,
				fromContactEmail: slackUser.email,
			}
		}
		// Posted by our app with nobody behind it, like a close-loop follow-up; Slack shows the app name.
		if (!slackMessage.slackUserId) return { fromContactName: "Productlane" }
		return {}
	}

	if (slackUser) {
		const isThreadContact =
			!!slackUser.email &&
			normalizeEmail(slackUser.email) ===
				normalizeEmail(threadContact?.email ?? "")
		return {
			fromContactName: slackUser.name ?? slackUser.username,
			fromContactImage:
				slackUser.imageUrl ??
				(isThreadContact ? threadContact?.imageUrl : undefined),
			fromContactEmail: slackUser.email,
		}
	}

	// Portal and widget replies reach Slack through our own app, and API imports
	// carry a synthetic author id, so neither has a Slack user; the thread contact wrote them.
	if (
		(isProductlaneMirroredSlackMessage(slackMessage) ||
			isImportedSlackMessage(slackMessage)) &&
		threadContact
	) {
		return {
			fromContactName:
				threadContact.name ??
				(threadContact.email
					? generateNameFromEmail(threadContact.email)
					: undefined),
			fromContactImage: threadContact.imageUrl,
			fromContactEmail: threadContact.email,
		}
	}

	return {}
}

// threadWithFullDetails syncs message relations newest-first (capped pages);
// event building and isLast assume chronological order, so re-sort here.
const sortByCreatedAtAsc = <T extends { createdAt: number; id: string }>(
	rows: readonly T[] | undefined,
): T[] =>
	[...(rows ?? [])].sort(
		(a, b) => a.createdAt - b.createdAt || a.id.localeCompare(b.id),
	)

// emails, comments etc. should be pre-filtered by thread
// Attribution for a specific message, so it renders on that message instead of
// as its own timeline row.
const MESSAGE_ATTRIBUTION_EVENT_TYPES = new Set([
	"ai_auto_reply_connectors",
	"ai_reply_trace",
])

export function isMessageAttributionActivity(eventType: string): boolean {
	return MESSAGE_ATTRIBUTION_EVENT_TYPES.has(eventType)
}

// Slack and Teams bot messages also carry the out-of-office notice, so only
// the trace row says which of them hold an agent answer.
export function agentReplyMessageIds(
	activities: readonly ThreadActivityRow[] | undefined,
): Set<string> {
	const ids = new Set<string>()
	for (const activity of activities ?? []) {
		if (activity.isDeleted || activity.eventType !== "ai_reply_trace") continue
		const payload = activity.payload as {
			messageId?: unknown
			outOfOfficeOnly?: unknown
		} | null
		if (payload?.outOfOfficeOnly === true) continue
		if (typeof payload?.messageId === "string") ids.add(payload.messageId)
	}
	return ids
}

/** Agent answers, the out-of-office notices it posted, and the close-loop follow-ups it sent. */
export function agentAuthoredMessageIds(
	activities: readonly ThreadActivityRow[] | undefined,
): Set<string> {
	const ids = agentReplyMessageIds(activities)
	for (const activity of activities ?? []) {
		if (activity.isDeleted) continue
		const payload = activity.payload as {
			messageId?: unknown
			outOfOfficeOnly?: unknown
		} | null
		if (
			activity.eventType === "ai_reply_trace" &&
			payload?.outOfOfficeOnly === true &&
			typeof payload.messageId === "string"
		) {
			ids.add(payload.messageId)
			continue
		}
		if (activity.eventType !== "close_loop_sent") continue
		const messageId = (activity.payload as { messageId?: unknown } | null)
			?.messageId
		if (typeof messageId === "string") ids.add(messageId)
	}
	return ids
}

export function connectorSourcesByEmail(
	activities: readonly ThreadActivityRow[] | undefined,
): Map<string, ConnectorAttribution[]> {
	const byEmailId = new Map<string, ConnectorAttribution[]>()
	for (const activity of activities ?? []) {
		if (activity.isDeleted) continue
		if (activity.eventType !== "ai_auto_reply_connectors") continue
		let payload: { emailId: string; connectors: ConnectorAttribution[] }
		try {
			payload = parseThreadActivity(activity.eventType, activity.payload)
				.payload as typeof payload
		} catch {
			continue
		}
		if (payload.connectors.length === 0) continue
		byEmailId.set(payload.emailId, [
			...(byEmailId.get(payload.emailId) ?? []),
			...payload.connectors,
		])
	}
	return byEmailId
}

// The breached response deadline a thread should surface, or null when it owes
// no breach: it is closed, we already replied, or the deadline has not passed.
export function resolveSyntheticSlaBreachDeadline(
	thread: Pick<
		Feedback,
		| "state"
		| "replied"
		| "slaDeadlineAt"
		| "lastInboundMessageAt"
		| "lastOutboundMessageAt"
	>,
	nowMs: number = Date.now(),
): number | null {
	if (thread.state === "COMPLETED" || thread.state === "PROCESSED") return null
	const deadline = thread.slaDeadlineAt
	if (!deadline || deadline >= nowMs) return null
	if (
		!owesReplyAgainstDeadline({
			replied: thread.replied,
			slaDeadlineAt: deadline,
			lastInboundMessageAt: thread.lastInboundMessageAt,
			lastOutboundMessageAt: thread.lastOutboundMessageAt,
		})
	)
		return null
	return deadline
}

export function getTimelineEvents({
	thread,
	contacts,
	slackUsers,
	linearUsers,
	users,
	workspace,
}: {
	thread: AugmentedFeedback
	contacts: readonly Contact[]
	slackUsers: readonly SlackUser[]
	linearUsers?: readonly LinearUser[]
	users: Array<Pick<User, "id" | "name" | "imageUrl">>
	workspace?: Workspace & {
		emailSettings?:
			| ReadonlyArray<{
					primaryEmail: string
					brandedInboundEmailAddress: string
			  }>
			| null
			| undefined
	}
}) {
	// Index lookup arrays once. Without this, every email/comment/slack/chat/
	// activity event below does a linear .find() across the entire workspace's
	// contacts and slack users (often thousands of rows), turning timeline
	// recomputation into O(events × workspaceContacts).
	const usersById = new Map(users.map((u) => [u.id, u]))
	const linearUsersById = new Map<string, LinearUser>()
	for (const lu of linearUsers ?? []) {
		if (lu.linearUserId) linearUsersById.set(lu.linearUserId, lu)
	}
	const slackUsersBySlackId = new Map<string, SlackUser>()
	for (const su of slackUsers) {
		if (su.slackId) slackUsersBySlackId.set(su.slackId, su)
	}
	const contactsById = new Map(contacts.map((c) => [c.id, c]))
	const contactsByNormalizedEmail = new Map<string, Contact>()
	for (const c of contacts) {
		if (c.email) contactsByNormalizedEmail.set(normalizeEmail(c.email), c)
	}

	const connectorSourcesByEmailId = connectorSourcesByEmail(
		thread.threadActivities,
	)
	const agentReplyIds = agentReplyMessageIds(thread.threadActivities)
	const agentAuthoredIds = agentAuthoredMessageIds(thread.threadActivities)

	const workspaceSendAddresses = new Set(
		(workspace?.emailSettings ?? [])
			.flatMap((a) => [a.primaryEmail, a.brandedInboundEmailAddress])
			.filter((email): email is string => !!email)
			.map(normalizeEmail),
	)

	const createdEvent: TimelineEvent = {
		type: "createdAt",
		thread: {
			...thread,
			contactIsUnverified: thread.contactIsUnverified ?? false,
			creatorName: usersById.get(thread.reporterId ?? "")?.name ?? undefined,
			creatorImage:
				usersById.get(thread.reporterId ?? "")?.imageUrl ?? undefined,
		},
	}
	const emailEvents: TimelineEvent[] = sortByCreatedAtAsc(thread.emails).map(
		(email, index, emails) => {
			let fromContactName
			let fromContactImage
			let fromContactId
			const parsedFrom = EmailContactSchema.parse(email.from)
			const normalizedFromEmail = normalizeEmail(parsedFrom.email)
			const isFromWorkspaceSendAddress =
				workspaceSendAddresses.has(normalizedFromEmail)

			if (email.source === "AUTO_REPLY" || email.source === "AI") {
				fromContactName = parsedFrom.name || workspace?.name
				fromContactImage = workspace?.logoUrl
			} else {
				if (email.type === "OUTBOUND") {
					const user = email.userId ? usersById.get(email.userId) : undefined
					fromContactName = user?.name
					fromContactImage = user?.imageUrl
					fromContactId = user?.id
				} else if (isFromWorkspaceSendAddress) {
					fromContactImage = workspace?.logoUrl
				} else {
					const contact = contactsByNormalizedEmail.get(normalizedFromEmail)
					fromContactName = contact?.name
					fromContactImage = contact?.imageUrl
				}
			}

			const isRealName = (name: string | undefined | null): boolean =>
				!!name && name !== parsedFrom.email && !name.includes("@")

			const displayName: string =
				isRealName(fromContactName) && fromContactName
					? fromContactName
					: isRealName(parsedFrom.name) && parsedFrom.name
						? parsedFrom.name
						: generateNameFromEmail(parsedFrom.email)

			return {
				type: "email",
				email: {
					...email,
					isLast: index === emails.length - 1,
					fromContactName: displayName,
					fromContactImage,
					fromContactId,
				},
				connectorSources: connectorSourcesByEmailId.get(email.id),
			}
		},
	)

	const enrichComment = (comment: Comment) => {
		const user = comment.userId ? usersById.get(comment.userId) : undefined
		const linearUser = comment.linearAuthorId
			? linearUsersById.get(comment.linearAuthorId)
			: undefined
		return {
			...comment,
			userName: user?.name ?? linearUser?.name,
			userImage: user?.imageUrl ?? linearUser?.avatarUrl,
		}
	}

	const sortedComments = sortByCreatedAtAsc(thread.comments)
	const commentsById = new Map(sortedComments.map((c) => [c.id, c]))

	// Collapse any parent chain to its top-level ancestor. Linear comment threads
	// are one level deep, so a reply to a reply still attaches to the thread root.
	const rootIdOf = (comment: Comment): string => {
		let current = comment
		const seen = new Set<string>()
		while (
			current.parentId &&
			commentsById.has(current.parentId) &&
			!seen.has(current.id)
		) {
			seen.add(current.id)
			current = commentsById.get(current.parentId)!
		}
		return current.id
	}

	const repliesByRoot = new Map<string, ReturnType<typeof enrichComment>[]>()
	for (const comment of sortedComments) {
		const rootId = rootIdOf(comment)
		if (rootId !== comment.id) {
			const arr = repliesByRoot.get(rootId) ?? []
			arr.push(enrichComment(comment))
			repliesByRoot.set(rootId, arr)
		}
	}

	const commentEvents: TimelineEvent[] = sortedComments
		.filter((comment) => rootIdOf(comment) === comment.id)
		.map((comment) => ({
			type: "comment",
			comment: enrichComment(comment),
			replies: repliesByRoot.get(comment.id) ?? [],
		}))

	const threadMessageEvents: TimelineEvent[] = sortByCreatedAtAsc(
		thread.threadMessages,
	).map((threadMessage) => {
		return {
			type: "threadMessage",
			threadMessage: threadMessage,
		}
	})

	const threadContact =
		thread.contact ??
		(thread.contactId ? contactsById.get(thread.contactId) : undefined)

	const slackMessageEvents: TimelineEvent[] = sortByCreatedAtAsc(
		thread.slackMessages ?? [],
	).map((slackMessage) => {
		const sender = resolveSlackMessageSender({
			slackMessage,
			usersById,
			slackUsersBySlackId,
			threadContact,
		})
		return {
			type: "slackMessage",
			slackMessage: {
				...slackMessage,
				...sender,
				fromContactName: sender.fromContactName
					? sender.fromContactName
					: "Unknown user",
			},
			isAgentReply: agentReplyIds.has(slackMessage.id),
			isFromAgent: agentAuthoredIds.has(slackMessage.id),
		}
	})

	const chatMessageEvents: TimelineEvent[] = sortByCreatedAtAsc(
		thread.chatMessages,
	).map((chatMessage) => {
		let fromContactName = chatMessage.authorName ?? "Unknown user"
		let fromContactImage: string | null | undefined
		if (chatMessage.type === "OUTBOUND") {
			const user = chatMessage.userId
				? usersById.get(chatMessage.userId)
				: undefined
			if (user?.name) fromContactName = user.name
			fromContactImage = user?.imageUrl
		} else {
			// Inbound - fall back to the thread's contact so Teams replies
			// show the Entra profile photo we synced on contact creation.
			const contact = thread.contactId
				? contactsById.get(thread.contactId)
				: undefined
			fromContactImage = contact?.imageUrl
		}
		return {
			type: "chatMessage",
			chatMessage: {
				...chatMessage,
				fromContactName,
				fromContactImage,
			},
			isAgentReply: agentReplyIds.has(chatMessage.id),
			isFromAgent: agentAuthoredIds.has(chatMessage.id),
		}
	})

	const liveChatEvents: TimelineEvent[] = thread.liveChatSession
		? [thread.liveChatSession].flatMap((liveChat) => {
				const events: TimelineEvent[] = []

				// Add individual message events
				sortByCreatedAtAsc(liveChat.messages).forEach((message) => {
					events.push({
						type: "live-chat-message" as const,
						liveChatMessage: message,
						contactIsUnverified: thread.contactIsUnverified ?? false,
					})
				})

				// Add session closed event if the session is finished
				if (liveChat.isFinished) {
					events.push({
						type: "live-chat-closed" as const,
						liveChat: liveChat,
					})
				}

				return events
			})
		: []

	const activityEvents: ActivityEvent[] = sortByCreatedAtAsc(
		thread.threadActivities,
	)
		.filter(
			(threadActivity) =>
				!isMessageAttributionActivity(threadActivity.eventType),
		)
		.map((threadActivity) => {
			const actor = threadActivity.actorId
				? usersById.get(threadActivity.actorId)
				: undefined
			const p = threadActivity.payload as Record<string, unknown>
			const assigneeId = (p.newAssigneeId ?? p.assigneeId) as string | undefined
			const assignee = assigneeId ? usersById.get(assigneeId) : undefined
			return {
				type: "activity" as const,
				threadActivity: {
					...threadActivity,
					actorName: actor?.name ?? null,
					actorImage: actor?.imageUrl ?? null,
					newAssigneeName: assignee?.name ?? null,
				},
			}
		})

	// Inject synthetic sla_breached when deadline is past and no real one exists.
	const breachedDeadline = resolveSyntheticSlaBreachDeadline(thread)
	if (
		breachedDeadline !== null &&
		!(thread.threadActivities ?? []).some(
			(a) =>
				a.eventType === "sla_breached" &&
				!a.isDeleted &&
				(a.payload as { deadlineAt?: string })?.deadlineAt ===
					new Date(breachedDeadline).toISOString(),
		)
	) {
		const syntheticRow = {
			id: `synthetic-sla-breach-${thread.id}`,
			createdAt: breachedDeadline,
			updatedAt: breachedDeadline,
			version: 0,
			workspaceId: thread.workspaceId,
			threadId: thread.id,
			eventType: "sla_breached",
			actorId: null,
			isDeleted: false,
			payload: {
				deadlineAt: new Date(breachedDeadline).toISOString(),
			},
		}
		activityEvents.push({
			type: "activity",
			threadActivity: {
				...syntheticRow,
				actorName: null,
				actorImage: null,
			},
		})
	}

	const messageEvents = [
		...emailEvents,
		...commentEvents,
		...threadMessageEvents,
		...slackMessageEvents,
		...chatMessageEvents,
		...liveChatEvents,
	]

	// A burst or merged change must not span a message, or it would sort after
	// one while holding members from before it.
	const splitAt = messageEvents.map(timelineEventTimestamp)
	const aggregatedActivities = collapseRoutineRuns(
		collapseAgentActivityBursts(
			aggregateActivityEvents(activityEvents, undefined, { splitAt }),
			{ splitAt },
		),
	)

	const allEvents = [createdEvent, ...messageEvents, ...aggregatedActivities]

	allEvents.sort(
		(a, b) => timelineEventTimestamp(a) - timelineEventTimestamp(b),
	)
	return allEvents
}
